// src/exercises/VoiceCoachStrip.jsx
// ══════════════════════════════════════════════════════════════════════════════
// 🎚️ LA TIRA DEL ENTRENADOR DE VOZ
//
// Aparece SOLO después de hablar, debajo del cuadro de texto, y es una fila.
// Es un plus, no el protagonista: el alumno viene a conversar con Spacy, no a
// mirar un panel de instrumentos. Si esto llamara más la atención que la
// conversación, estaría mal hecho.
//
// ── DE DÓNDE SALE CADA NÚMERO ────────────────────────────────────────────────
//   Claridad  → confianza media que Deepgram dio a cada palabra. Es lo más
//               cerca que se puede estar de medir pronunciación sin un modelo
//               fonético, y no cuesta ni una llamada: venía en la respuesta.
//   Fluidez   → ritmo (palabras por minuto) penalizado por muletillas y por
//               frenazos largos. También sale de los tiempos de cada palabra.
//   Orden     → esto SÍ necesita un modelo de lenguaje, así que es un botón.
//               El alumno decide si lo gasta.
//
// Lo que NO se hace: pedirle a un modelo de texto que puntúe una pronunciación
// que nunca oyó. Sería un número inventado con bata de laboratorio.
//
// ── POR QUÉ NO SE SUSTITUYE EL TEXTO SIN MÁS ─────────────────────────────────
// Al ordenar, la versión limpia se enseña AL LADO de la suya, no encima. Quien
// aprende necesita ver la diferencia entre lo que dijo y lo que quería decir;
// si se le cambia el texto sin enseñárselo, sólo aprende a pulsar un botón.
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { Sparkles, Check, X, Loader, Gauge, Waves } from 'lucide-react';
import './voice-coach.css';

const API = import.meta.env.VITE_API_URL || '';

export default function VoiceCoachStrip({ metrics, transcript, level, onUseText }) {
    const [ordenando, setOrdenando] = useState(false);
    const [propuesta, setPropuesta] = useState(null);
    const [error, setError] = useState('');

    // Sin nada que decir, no se ocupa sitio.
    if (!transcript) return null;

    const ordenar = async () => {
        setOrdenando(true);
        setError('');
        try {
            const r = await fetch(`${API}/api/tutor/polish`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify({ text: transcript, level: level || 'A2' }),
            });
            if (!r.ok) throw new Error(String(r.status));
            const datos = await r.json();
            setPropuesta(datos);
        } catch {
            setError('No se pudo ordenar el texto. Tu versión sigue intacta.');
        } finally {
            setOrdenando(false);
        }
    };

    const aceptar = () => {
        if (propuesta?.polished) onUseText?.(propuesta.polished);
        setPropuesta(null);
    };

    return (
        <div className="vc-strip">
            <div className="vc-row">
                {metrics && (
                    <>
                        <span className={`vc-chip vc-${metrics.claridad_nivel}`} title="Qué tan claro se te entendió cada palabra">
                            <Gauge size={13} />
                            Claridad <b>{Math.round(metrics.claridad)}%</b>
                        </span>
                        <span className={`vc-chip vc-${metrics.fluidez_nivel}`} title="Ritmo, muletillas y pausas largas">
                            <Waves size={13} />
                            Fluidez <b>{Math.round(metrics.fluidez)}%</b>
                        </span>
                        <span className="vc-chip vc-dato" title="Palabras por minuto">
                            {metrics.wpm} ppm
                        </span>
                        {metrics.muletillas > 0 && (
                            <span
                                className="vc-chip vc-dato"
                                title={`Muletillas detectadas: ${metrics.muletillas_lista.join(', ')}`}
                            >
                                {metrics.muletillas} muletilla{metrics.muletillas > 1 ? 's' : ''}
                            </span>
                        )}
                    </>
                )}

                <button className="vc-btn" onClick={ordenar} disabled={ordenando}>
                    {ordenando ? <Loader size={13} className="spin-animation" /> : <Sparkles size={13} />}
                    {ordenando ? 'Ordenando…' : 'Ordenar ideas'}
                </button>
            </div>

            {error && <div className="vc-error">{error}</div>}

            {propuesta && (
                <div className="vc-propuesta">
                    <div className="vc-propuesta-cabecera">
                        <span>Versión ordenada</span>
                        {propuesta.coherence > 0 && (
                            <span className="vc-chip vc-dato">
                                Hilo de ideas <b>{propuesta.coherence}%</b>
                            </span>
                        )}
                    </div>

                    <p className="vc-propuesta-texto">{propuesta.polished}</p>

                    {propuesta.coherence_reason && (
                        <p className="vc-propuesta-nota">{propuesta.coherence_reason}</p>
                    )}

                    {propuesta.changes?.length > 0 && (
                        <ul className="vc-cambios">
                            {propuesta.changes.map((c, i) => <li key={i}>{c}</li>)}
                        </ul>
                    )}

                    <div className="vc-propuesta-acciones">
                        <button className="vc-btn vc-btn-si" onClick={aceptar}>
                            <Check size={13} /> Usar esta versión
                        </button>
                        <button className="vc-btn vc-btn-no" onClick={() => setPropuesta(null)}>
                            <X size={13} /> Dejar la mía
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
